
/* eslint-disable max-len */
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useObserver, useObserverReturningStore } from '../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../Legacy';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { Badge } from '../../../ui/Badge';
import { GridButton } from '../../../ui/GridButton';
import { useResolve } from '../../Container';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { concealmentToggle } from '../../packages/conceal/ConcealCommands';
import { ConcealmentInfo } from '../../packages/conceal/ConcealmentInfo';
import { FeatureManager } from '../../packages/feature/FeatureManager';
import { incognitoToggle } from '../../packages/incognito/IncognitoCommands';
import { IncognitoInfo } from '../../packages/incognito/IncognitoInfo';
import { ReadyCheckController } from '../../packages/readyCheck/ReadyCheckController';
import { useEditorSessionObserver } from '../EditorSession';
import { Cell, Colors, Toggle } from './../../../ui';
import { meetingToggleTimer } from './MeetingCommands';
import { useActiveMeeting } from './MeetingHooks';

const Grid = styled.div`
    display: grid;
    grid-template-columns: auto auto;
    column-gap: 1rem;
    row-gap: 1rem;
`;

type ItemProps = {
    label: string,
    desc: string,
    icon: string,
    trackAs?: string,
    active?: boolean,
    disabled?: boolean,
    toggle?: boolean,
    id?: string,
    onSelect: () => void,
};

const Item = React.memo<ItemProps>(({ label, desc, icon, trackAs, active, disabled, toggle, id, onSelect }) =>
    <Cell id={id ? id : null} data-action={trackAs}>
        <GridButton disabled={disabled} onClick={onSelect}>
            <GridButton.Top color={Colors.primary}>
                <GridButton.Icon color={Colors.primary}>
                    <Icon color={Colors.primary} size="2.4" src={icon} />
                </GridButton.Icon>
                <Title>{label}</Title>
                {(toggle === true || toggle === false) && <div style={{ marginLeft: 'auto' }}>
                    <Toggle value={toggle} />
                </div>}
                {(active === true) && <div style={{ marginLeft: 'auto' }}>
                    <Badge color={Colors.positive}>ACTIVE</Badge>
                </div>}
            </GridButton.Top>
            <Copy>{desc}</Copy>
        </GridButton>
    </Cell>
);

const menuItem = (label: string, desc: string, icon: string, menuKey: string, trackAs: string) =>
    React.memo<{ onSelect: any }>(({ onSelect }) => <Item {...{ label, desc, icon, trackAs }} onSelect={() => onSelect(menuKey)} />);

const ChangeToolsItem = menuItem(
    'Change Tools',
    'Change the topic or tools available to the meeting participants.',
    'deqo://icons/meeting/activity',
    'menu/settings',
    'editor.hostMenu.openTools',
);

const BreaktimeItem = menuItem(
    'Coffee Break',
    'Pause to allow everyone to grab coffee, lunch or slice of delicious cake...',
    'deqo://icons/meeting/coffee',
    'menu/breaktime',
    null,
);

const HostsItem = menuItem(
    'Hosts',
    'Add or remove fellow hosts to help run the best meeting ever!',
    'deqo://icons/meeting/facilitator',
    'menu/hosts',
    'editor.hostMenu.openHosts',
);

const RunPollItem = menuItem(
    'Run Poll',
    'Anonymously ask your participants a question and view the results.',
    'deqo://icons/meeting/poll',
    'menu/poll',
    'editor.hostMenu.openPolls',
);

const FinishItem = menuItem(
    'End Meeting',
    'Finish the meeting and go back to using more boring software.',
    'deqo://icons/meeting/finish',
    'menu/finish',
    null
);

const PrivateWritingItem = React.memo(() => {
    const dispatch = useResolve(CommandDispatcher);
    const { enabled } = useEditorSessionObserver(ConcealmentInfo);
    const features = useObserver(useResolve(FeatureManager));

    return <Item
        label="Private Writing"
        desc="Enable/disable private writing mode."
        icon="deqo://icons/meeting/private"
        trackAs="editor.hostMenu.togglePrivateWriting"
        disabled={features.get('Hands')}
        toggle={enabled && !features.get('Hands')}
        onSelect={() => dispatch(concealmentToggle)}
    />;
});

const ReadyCheckItem = React.memo(() => {
    const ctrl = useObserverReturningStore(useResolve(ReadyCheckController));
    const toggle = useCallback(() => {
        if (ctrl.active) ctrl.finish();
        else ctrl.startNew();
    }, [ctrl]);
    return <Item
        label="Ready Check"
        desc="Provides a button participants can use mark themselves as ready to continue."
        icon="deqo://icons/meeting/ready"
        trackAs="editor.hostMenu.toggleReadyCheck"
        toggle={ctrl.active}
        id="ready-check"
        onSelect={toggle}
    />;
});

const IncognitoItem = React.memo(() => {
    const dispatch = useResolve(CommandDispatcher);
    const { enabled } = useEditorSessionObserver(IncognitoInfo);
    return <Item
        label="Hide Identities"
        desc="Create a safe space for sharing candid feedback by making everyone anonymous."
        icon="deqo://icons/meeting/anonymous"
        trackAs="editor.hostMenu.toggleIncognito"
        toggle={enabled}
        onSelect={() => dispatch(incognitoToggle)}
    />;
});

const TimerItem = React.memo(() => {
    const dispatch = useResolve(CommandDispatcher);
    const { timer: enabled } = useActiveMeeting();
    return <Item
        label="Meeting Timer"
        desc="Enable/disable the meeting timer, which you can use to keep time!"
        icon="deqo://icons/meeting/timer"
        trackAs="editor.hostMenu.toggleMeetingTimer"
        toggle={enabled}
        id="meeting-timer"
        onSelect={() => dispatch(meetingToggleTimer)}
    />;
});

export const ManageActiveMeetingFacilitatorMenu: LFC<{ onNavigate: (view: string) => void }> = ({ onNavigate: onSelect }) =>
    <Flexer flex="column" padding="1">
        <Grid>
            <ChangeToolsItem onSelect={onSelect} />
            <HostsItem onSelect={onSelect} />
            <PrivateWritingItem />
            <TimerItem />
            <IncognitoItem />
            <ReadyCheckItem />
            <RunPollItem onSelect={onSelect} />
            <FinishItem onSelect={onSelect} />
        </Grid>
    </Flexer >;
